import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedDate, FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import { SimpleLoadingComponent } from '../../utils/apollo';
import Container from '../../components/Container';
import Divider from '../../components/Divider';
import EventDetails from './event-details';
import InternalNotes from './event-notes';
import messages from './messages';
import Button from '../../components/Button';
import Toast, { ToastKind } from '../../components/Toast';
import SubscriptionActive from '../../components/SubscriptionActive';
import { REP_VISITS_SUBSCRIPTION } from '../Authorization/constants';
import updateRSVPCollegeFairMutation from '../../mutations/HighSchool/updateRsvpCollegeFair';
import cancelRsvpCollegeFairMutation from '../../mutations/HighSchool/cancelRsvpCollegeFair';
import EventVisitCancel from './event-visit-cancel';
import { deadlinePassed } from './util';

import './styles.css';

export class EventFairDetails extends React.PureComponent {
  static propTypes = {
    node: PropTypes.object,
    onSave: PropTypes.func,
    mutateCollegeFairRSVP: PropTypes.func.isRequired,
    cancelCollegeFairRSVP: PropTypes.func.isRequired,
  };

  state = {
    notes: get(this.props, 'node.notes', ''),
    cancelling: false,
    executingCancellation: false,
  };

  cancelSuccessNotification = (
    <Toast
      active
      title=""
      description={<FormattedMessage {...messages.cancellationSuccessFair} />}
      kind={ToastKind.success}
    />
  );

  cancelFailureNotification = (
    <Toast
      active
      title=""
      description={<FormattedMessage {...messages.cancellationFailFair} />}
      kind={ToastKind.error}
    />
  );

  successNotification = (
    <Toast
      active
      title={<FormattedMessage {...messages.saveSuccessTitle} />}
      description={<FormattedMessage {...messages.saveSuccessDescription} />}
      kind={ToastKind.success}
    />
  );

  failureNotification = (
    <Toast
      active
      title=""
      description={<FormattedMessage {...messages.saveFail} />}
      kind={ToastKind.error}
    />
  );

  onChange = (event) => {
    this.setState({ notes: event.target.value });
  };

  onCancel = (cancellationMessage) => {
    const { node, cancelCollegeFairRSVP } = this.props;
    this.setState({ executingCancellation: true });

    cancelCollegeFairRSVP({
      variables: {
        input: {
          cancellationMessage,
          collegeFair: {
            id: node.id,
          },
        },
      },
    }).then(this.onCancelSuccess, this.onCancelFailure);
  };

  onCancelSuccess = () => {
    this.props.onSave(this.cancelSuccessNotification);
    this.setState({ executingCancellation: false });
  };

  onCancelFailure = () => {
    this.props.onSave(this.cancelFailureNotification);
    this.setState({ executingCancellation: false });
  };

  toggleCancelling = () => {
    this.setState((prevState) => ({ cancelling: !prevState.cancelling }));
  };

  saveNotes = () => {
    const { onSave, mutateCollegeFairRSVP, node } = this.props;
    const { notes } = this.state;
    const onFailure = () => { onSave(this.failureNotification); };
    const onSuccess = () => { onSave(this.successNotification); };
    mutateCollegeFairRSVP({
      variables: {
        input: {
          collegeFair: {
            id: node.id,
          },
          notes,
        },
      },
    }).then(onSuccess, onFailure);
  };

  render() {
    const { node } = this.props;
    const { cancelling, executingCancellation } = this.state;

    const startDay = get(node, 'date');
    const startTime = get(node, 'startISOTime');
    const eventDetails = {
      contact: (get(node, 'highSchool.repVisitsAccount.collegeFairsPrimaryContact') ||
        get(node, 'highSchool.repVisitsAccount.primaryContact')),
      phone: (get(node, 'highSchool.repVisitsAccount.collegeFairsPrimaryContactPhone') ||
        get(node, 'highSchool.repVisitsAccount.primaryContactPhone') || 'N/A'),
      cost: get(node, 'cost'),
      expectedStudents: get(node, 'numberOfExpectedStudents'),
      instructions: get(node, 'webInstructions'),
      title: get(node, 'name'),
      timezone: get(node, 'timezone'),
      startISOTime: startTime,
      endISOTime: get(node, 'endISOTime'),
      date: startDay,
    };
    return (
      cancelling ? (
        <Container styleName="sidebar">
          <EventVisitCancel
            title={<FormattedMessage {...messages.fairDetails} />}
            cancelButtonLabel={<FormattedMessage {...messages.confirmCancellationFair} />}
            onCancel={this.onCancel}
            goBack={this.toggleCancelling}
            highSchool={get(node, 'highSchool')}
            eventDetails={eventDetails}
            loading={executingCancellation}
          />
        </Container>
      ): (
        <Container styleName="sidebar">
          <EventDetails type="fair" highSchool={get(node, 'highSchool')} eventDetails={eventDetails} />
          <InternalNotes onChange={this.onChange} hsNotes={this.state.notes} />
          <div styleName="reschedule-and-cancel">
            <div styleName="section-title">
              <FormattedMessage {...messages.cancellation} />
            </div>
            <div styleName="grey-text">
              {deadlinePassed(new Date(node.rsvpDeadline)) ?
                <FormattedMessage {...messages.cancelDeadlinePassed} /> : (
                  <span>
                    <FormattedMessage
                      {...messages.cancellationDetailsFair}
                      values={{
                        date: <FormattedDate value={`${node.rsvpDeadline}T12:00:00`} year="numeric" day="2-digit" month="short" />,
                      }}
                    />
                    <Button negative onClick={this.toggleCancelling}>
                      <FormattedMessage {...messages.cancellationLinkFair} />
                    </Button>
                  </span>
                )}
            </div>
          </div>
          <Divider section />
          <SubscriptionActive anyOf={REP_VISITS_SUBSCRIPTION}>
            <Button onClick={this.saveNotes} floated="right" primary>
              <FormattedMessage {...messages.save} />
            </Button>
          </SubscriptionActive>
        </Container>
      ));
  }
}

const query = gql`
  query EventFair($id: ID!) {
    node(id: $id) {
      id
      ...on CollegeFair {
        cost
        numberOfExpectedStudents
        name
        date
        rsvpDeadline
        timezone
        webInstructions
        notes
        startISOTime
        endISOTime
        highSchool {
          id
          name
          address {
            field_hs_elna_address_line1
            field_hs_elna_state_latest
            field_hs_elna_city
            field_hs_eln_zip
          }
          repVisitsAccount {
            id
            primaryContactPhone
            primaryContact {
              id
              name
              field_job_position
              field_contact_work
              field_profile_picture_file_url
            }
            collegeFairsPrimaryContact {
              id
              name
              field_job_position
              field_contact_work
              field_profile_picture_file_url
            }
            collegeFairsPrimaryContactPhone
          }
        }
      }
    }
  }
`;

const graphqlData = graphql(query, {
  options: ({ id }) => ({
    variables: {
      id,
    },
    fetchPolicy: 'cache-and-network',
  }),
});

const mutateCollegeFairRSVP = graphql(updateRSVPCollegeFairMutation, {
  name: 'mutateCollegeFairRSVP',
  options: { refetchQueries: ['NotificationCount'] },
});

const cancelCollegeFairRSVP = graphql(cancelRsvpCollegeFairMutation, {
  name: 'cancelCollegeFairRSVP',
  options: {
    refetchQueries: [
      'RepVisitsOverview',
      'AvailabilityCalendar',
      'RepVisitsTravelPlanSearch',
      'RepVisitsEvents',
      'EventsAndNotifications',
      'NotificationCount',
    ],
  },
});

export default SimpleLoadingComponent(graphqlData, mutateCollegeFairRSVP, cancelCollegeFairRSVP)(EventFairDetails);
